import { api } from "./api";
import { userStore } from "../ducks/user";
import { v4 } from "uuid";
// import posthog from "posthog-js"; // or wherever you exported your initialised client

// variant you want to trigger

import {
  doStartReconnectTimer,
  endReconnectTimer,
  setOtherLoginShow,
  setWebsocketUrl,
  updateReconnectTimer,
  wsConnectionStore,
} from "../ducks/wsConnection";

import { routeIncommingMessage } from "./wsInRoutes";
import { LoginWs } from "./wsOutRoutes";
import {
  ERROR_WEBSOCKET_CONNECTION_TIMEOUT,
  ERROR_JAVASCRIPT_ERROR,
} from "../ducks/ping";
// import { longPollingGetRequest } from "./longPolling";

// const EXPERIMENT_FLAG = "longpoll-vs-websocket"; // your flag key
// const TEST_VARIANT = "test";

// function runDisconnectExperiment(gameId: string) {
//   const variant = posthog.getFeatureFlag(EXPERIMENT_FLAG); // returns string | boolean | undefined

//   // If flags haven’t finished loading yet, wait once and retry
//   if (variant === undefined) {
//     posthog.onFeatureFlags(() => runDisconnectExperiment(gameId));
//     return;
//   }

//   if (variant === TEST_VARIANT) {
//     // user is in “test” → switch transport
//     switchToLongPolling(gameId);
//   }
// }

let offlineDetectionInitialized = false;
let offlineTimeOut;
let connectionTimeout: any;
let connectionAttemptErrors: any[] = [];
let globalErrorHandlersInitialized = false;
let recentJsErrorReports: number[] = [];
const MAX_JS_ERROR_REPORTS_PER_MINUTE = 5;

function shouldReportJSError(): boolean {
  const now = Date.now();
  const oneMinuteAgo = now - 60000;

  // Remove reports older than 1 minute
  recentJsErrorReports = recentJsErrorReports.filter(
    (timestamp) => timestamp > oneMinuteAgo
  );

  // Check if we're under the limit
  if (recentJsErrorReports.length < MAX_JS_ERROR_REPORTS_PER_MINUTE) {
    recentJsErrorReports.push(now);
    return true;
  }

  return false;
}

function setupGlobalErrorHandlers() {
  if (globalErrorHandlersInitialized || typeof window === "undefined") return;
  globalErrorHandlersInitialized = true;

  // Capture unhandled JavaScript errors
  window.addEventListener("error", (event) => {
    const now = Date.now();
    const errorDetails = {
      type: "global_javascript_error",
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      timestamp: now,
    };

    // Skip reporting cross-origin script errors (third-party scripts)
    const isCrossOriginScriptError = event.message === "Script error.";
    if (isCrossOriginScriptError) {
      console.log(
        "Skipped reporting cross-origin script error (third-party script)"
      );
      return; // Skip both reporting and connection analysis collection
    }

    // Report general JavaScript errors with throttling
    if (shouldReportJSError()) {
      import("../ducks/ping").then((module) => {
        module.setError(ERROR_JAVASCRIPT_ERROR);
        module.setErrorData({
          jsError: {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            stack: event.error?.stack,
          },
          jsErrorType: "unhandled_error",
          jsErrorTimestamp: now,
        });
        module.sendPingData();
      });
    }

    // Also collect for connection analysis if during connection attempts
    const recentErrors = connectionAttemptErrors.filter(
      (e) => now - e.timestamp < 30000
    ); // 30 seconds
    if (recentErrors.length > 0 || connectionTimeout) {
      connectionAttemptErrors.push(errorDetails);
    }
  });

  // Capture unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const now = Date.now();
    const rejectionDetails = {
      type: "unhandled_promise_rejection",
      reason: event.reason?.toString() || "Unknown rejection",
      stack: event.reason?.stack,
      timestamp: now,
    };

    // Report general promise rejections with throttling
    if (shouldReportJSError()) {
      import("../ducks/ping").then((module) => {
        module.setError(ERROR_JAVASCRIPT_ERROR);
        module.setErrorData({
          jsError: {
            message: rejectionDetails.reason,
            stack: rejectionDetails.stack,
          },
          jsErrorType: "unhandled_promise_rejection",
          jsErrorTimestamp: now,
        });
        module.sendPingData();
      });
    }

    // Also collect for connection analysis if during connection attempts
    const recentErrors = connectionAttemptErrors.filter(
      (e) => now - e.timestamp < 30000
    );
    if (recentErrors.length > 0 || connectionTimeout) {
      connectionAttemptErrors.push(rejectionDetails);
    }
  });
}

function setupOfflineDetection() {
  if (offlineDetectionInitialized) return;
  offlineDetectionInitialized = true;

  window.addEventListener("offline", (event) => {
    import("../ducks/ping").then((module) => {
      module.setOffline();
      module.setErrorData({
        networkState: "offline",
        networkStateTime: Date.now(),
      });

      offlineTimeOut = setTimeout(() => {
        startReconnectTimer();
      }, 3000);
    });
  });

  window.addEventListener("online", (event) => {
    import("../ducks/ping").then((module) => {
      module.setErrorData({
        networkState: "online",
        networkStateTime: Date.now(),
      });
      module.resetRetries();
      clearTimeout(offlineTimeOut);
      endReconnectTimer();
      // onOnline();
      initWebsocketConnect();
    });
  });
}

export let websocket = null;

export function getOrCreateSessionId() {
  let sessionId: string = sessionStorage.getItem("sessionId");
  if (!sessionId) {
    sessionId = v4();
    sessionStorage.setItem("sessionId", sessionId);
  }
  return sessionId;
}

async function loginServer() {
  const { username, isGuest, id, room } = userStore.getState();
  import("../ducks/gamePlay").then(({ gamePlayStore }) => {
    const { gameId } = gamePlayStore.getState();

    try {
      const sessionId = getOrCreateSessionId();

      const user = {
        username,
        isGuest,
        userId: id,
        room,
        gameId,
        sessionId,
      };

      LoginWs.loginServer(user);
    } catch (e) {
      setTimeout(() => {
        loginServer();
      }, 3000);
    }
  });
}

function startReconnectTimer() {
  const reconnectStart = wsConnectionStore.getState().reconnectStart;
  if (!reconnectStart) {
    doStartReconnectTimer();

    const loop = () => {
      const startTime = wsConnectionStore.getState().reconnectStart;
      if (startTime == null) {
        return;
      }
      updateReconnectTimer();
      requestAnimationFrame(loop);
    };

    loop();
  }
}

let pingInterval: any;
// export let useLongPolling = false;
let postTimeout: any;
let missedPongs = 0;
const maxMissedPongs = 3;

export function initWebsocketConnect() {
  setupGlobalErrorHandlers();

  api
    .get(`/site/settings?_=${new Date().getTime()}`)
    .then((response) => {
      const { ws } = response.data;
      setWebsocketUrl(ws);
      connectToWebSocket();
    })
    .catch((error) => {
      console.log("get ws number error", error);
      connectionAttemptErrors.push({
        type: "api_settings_error",
        error: error.message,
        stack: error.stack,
        timestamp: Date.now(),
      });
      setTimeout(() => {
        initWebsocketConnect();
      }, 1000);
    });
}

//Happens when server code update happens and it switches to a new server instance
export async function checkWs(ws: number) {
  import("../ducks/gamePlay").then(({ gamePlayStore }) => {
    const { gameEnded } = gamePlayStore.getState();

    const isPlaying = !gameEnded;
    if (isPlaying) {
      const unsubscribe = gamePlayStore.subscribe(({ gameEnded }) => {
        if (gameEnded && wsConnectionStore.getState().ws !== ws) {
          setTimeout(() => {
            unsubscribe();
            setWebsocketUrl(ws);
            connectToWebSocket();
          }, 2000);
        }
      });
    }

    if (!isPlaying && wsConnectionStore.getState().ws !== ws) {
      setWebsocketUrl(ws);
      connectToWebSocket();
    }
  });
}

export function otherUserLogin() {
  disconnectWebSocket();
  setOtherLoginShow(true);
}

function disconnectWebSocket() {
  // Clear connection timeout to avoid false positives
  clearTimeout(connectionTimeout);

  if (websocket) {
    websocket.onclose = function () {};
    websocket.close();
  }
}

// function onOnline() {
//   // location.reload();
//   console.log("onOnline");

//   // if (!useLongPolling) {
//   initWebsocketConnect(); // Immediately try to reconnect
//   // } else {
//   //   import("../ducks/gamePlay").then(async ({ gamePlayStore }) => {
//   //     const { gameId, turns, moves } = gamePlayStore.getState();
//   //     await switchToLongPolling(gameId);
//   //     GamePlayWs.sendGameSync(gameId, turns, moves);
//   //   });
//   // }
// }

// const initialDelay = 1000; // Start with a 1-second delay
// const maxDelay = 5000; // Cap the delay at 5 seconds
let retries = 0;
// export function getDelay() {
//   let delay = Math.min(maxDelay, initialDelay * Math.pow(2, retries));
//   delay = delay / 2 + (Math.random() * delay) / 2;
//   return delay;
// }
export function incrRetries() {
  retries++;
}

export function connectToWebSocket() {
  disconnectWebSocket();
  // useLongPolling = false;
  missedPongs = 0;

  // Clear any existing connection timeout and reset error collection
  clearTimeout(connectionTimeout);
  connectionAttemptErrors = [];

  // Start 10-second connection timeout
  connectionTimeout = setTimeout(() => {
    import("../ducks/ping").then((module) => {
      module.setError(ERROR_WEBSOCKET_CONNECTION_TIMEOUT);
      module.setErrorData({
        connectionTimeout: true,
        connectionTimeoutTime: Date.now(),
        connectionAttemptErrors:
          connectionAttemptErrors.length > 0
            ? connectionAttemptErrors
            : undefined,
        websocketUrl: wsConnectionStore.getState().websocketUrl,
      });
    });
  }, 10000);

  const url = wsConnectionStore.getState().websocketUrl;

  try {
    websocket = new window.WebSocket(url);
  } catch (error) {
    connectionAttemptErrors.push({
      type: "websocket_creation_error",
      error: error.message,
      stack: error.stack,
      timestamp: Date.now(),
    });
    // Still throw the error to trigger normal error handling
    throw error;
  }

  websocket.onerror = (error: Event) => {
    // Clear connection timeout since we got an error (different issue)
    clearTimeout(connectionTimeout);

    // Collect error details for analysis
    connectionAttemptErrors.push({
      type: "websocket_error_event",
      readyState: websocket?.readyState,
      url: websocket?.url,
      timestamp: Date.now(),
      error: error,
    });

    import("../ducks/ping").then((module) => {
      module.setWsError();
      module.setErrorData({
        lastWebsocketError: {
          readyState: websocket?.readyState,
          url: websocket?.url,
          timestamp: Date.now(),
        },
      });
    });
  };

  websocket.onclose = (e: CloseEvent) => {
    console.log("websocket close");

    // Clear connection timeout to avoid false positives
    clearTimeout(connectionTimeout);

    // Collect close event details
    if (e.code !== 1000) {
      // Only collect abnormal close events
      connectionAttemptErrors.push({
        type: "websocket_close_event",
        code: e.code,
        reason: e.reason,
        wasClean: e.wasClean,
        timestamp: Date.now(),
      });
    }

    import("../ducks/ping").then((module) => {
      import("../ducks/gamePlay").then(({ gamePlayStore }) => {
        const now = Date.now();
        module.setWsClose();
        module.setWsCloseErrorMsg(e.code + " " + e.reason);
        module.setDisconnectTime();
        module.setErrorData({
          eventSequence: {
            lastWsClose: now,
            timeSinceLastVisibilityChange:
              now -
              (module.pingStore.getState().errorData.connectionState
                .lastVisibilityChange || 0),
            timeSinceLastPing:
              now -
              (module.pingStore.getState().errorData.eventSequence
                .lastPingSent || 0),
            timeSinceLastPong:
              now -
              (module.pingStore.getState().errorData.eventSequence
                .lastPongReceived || 0),
          },
        });

        const { gameEnded, gameId } = gamePlayStore.getState();
        const isPlaying = gameEnded == false;
        module.setErrorData({
          wsCloseIsPlaying: isPlaying,
          wsCloseTime: now,
          wsCloseGameId: gameId,
        });

        setTimeout(() => {
          module.setRetries();
          retries++;
          startReconnectTimer();
          initWebsocketConnect();
        }, 1000);
      });
    });
  };
  websocket.onopen = () => {
    console.log("websocket open");

    // Clear connection timeout since we've successfully connected
    clearTimeout(connectionTimeout);

    endReconnectTimer();

    startPing();

    import("../ducks/lobby").then((module) => {
      module.clearInvites();
      module.enterLobby();
    });

    loginServer();
    setupOfflineDetection();
  };

  websocket.onmessage = async (e) => {
    if (e.data === "pong") {
      import("../ducks/ping").then((module) => {
        module.setErrorData({
          eventSequence: {
            lastPongReceived: Date.now(),
          },
        });
      });
      clearTimeout(postTimeout);
      return;
    }

    let data: any;
    try {
      data = JSON.parse(e.data);
    } catch (e) {
      console.log("JSON parse error", e);
      return;
    }
    routeIncommingMessage(data);
  };

  websocket.sendPing = () => {
    try {
      websocket.send("ping");
      import("../ducks/ping").then((module) => {
        module.setErrorData({
          eventSequence: {
            lastPingSent: Date.now(),
          },
        });
      });
    } catch (e) {
      setTimeout(() => {
        websocket.sendPing();
      }, 1000);
    }
  };
}

function startPing() {
  clearInterval(pingInterval);

  pingInterval = setInterval(() => {
    try {
      if (websocket && websocket.readyState == WebSocket.OPEN) {
        websocket.sendPing();

        // postTimeout = setTimeout(() => {
        //   import("../ducks/gamePlay").then(({ gamePlayStore }) => {
        //     const { gameEnded, gameId } = gamePlayStore.getState();
        //     if (gameEnded) {
        //       return;
        //     }

        //     missedPongs++;
        //     console.warn(`Missed pong (${missedPongs}/${maxMissedPongs})`);

        //     if (missedPongs >= maxMissedPongs) {
        //       // console.warn("Switching to long polling due to pong timeout");
        //       // if (useLongPolling == false) {
        //       // switchToLongPolling(gameId);
        //       // }
        //       // runDisconnectExperiment(gameId);
        //     }
        //   });
        // }, 2200); // Expect pong within 2 seconds
      }
    } catch (e) {
      console.log(e);
    }
  }, 5000);
}

// async function switchToLongPolling(gameId: string) {
//   console.log("switchToLongPolling");

//   clearInterval(pingInterval);
//   useLongPolling = true;
//   const username = userStore.getState().username;
//   const sessionId = sessionStorage.getItem("sessionId");

//   await longPollingGetRequest(username, sessionId, gameId);

//   disconnectWebSocket();
// }
